import { useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useCollectionBinding,
  useSlotGroup,
  EditableCollectionBinding,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { clean, stagger, galleryPhotos } from '../utils/format';
import { useMediaQuery, useRawProperty } from '../utils/hooks';
import { Lightbox, useLightbox } from '../utils/lightbox';
import { PATHS } from '../utils/site';

export interface Amenity {
  title?: string;
  group?: string;
  amenity_image?: string;
}

interface Photo {
  image?: string;
  alt_text?: string;
  description?: string;
}

const ctaDef: SlotGroupDefinition = {
  label: 'All Amenities Button',
  fields: {
    label: { label: 'Button Text', type: 'text', defaultValue: 'See All Amenities' },
    url: { label: 'URL', type: 'url', defaultValue: '/amenities' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const photoDef: SlotGroupDefinition = {
  label: 'Section Photo',
  description: 'Shown beside the amenity list until a visitor picks an amenity. Until set, a gallery photo is chosen automatically.',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 0, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt: "A gallery photo that shows this property's shared amenities or community spaces at their best. Prefer a real amenity over a home interior or plain exterior.",
    context: 'Tall photo beside the six-amenity list in the Amenities section of the home page, below the hero. The hero already uses the featured image, so pick a different photo.',
  },
};

/** Yardi's "Apartment" group reads as "Your home". */
export const groupLabel = (g?: string) => (g === 'Apartment' ? 'Your home' : g || 'Community');

const PRIORITY = [/yard|green space/i, /garage/i, /lawn|landscap/i, /smart|thermostat/i, /pool/i, /playground/i, /dog park|pet/i];
const STOP = new Set(['home', 'unit', 'site', 'with', 'technology', 'maintenance', 'management', 'alternative', 'security', 'deposit', 'ready', 'cable', 'green', 'building', 'large', 'high']);

/** An amenity's own photo, else the gallery photo whose alt text (then description) names it. */
export function amenityPhoto(a: Amenity, photos: Photo[]): { src: string; alt: string } | null {
  const own = clean(a.amenity_image);
  if (own) return { src: own, alt: a.title || '' };
  const words = (a.title || '').toLowerCase().split(/[^a-z]+/).filter((w) => w.length >= 4 && !STOP.has(w));
  if (!words.length) return null;
  const hit = (field: 'alt_text' | 'description') => photos.find((ph) => words.some((w) => new RegExp('\\b' + w, 'i').test(ph[field] || '')));
  const g = hit('alt_text') || hit('description');
  return g?.image ? { src: g.image, alt: g.alt_text || a.title || '' } : null;
}

export function AmenitiesList() {
  const p = useRawProperty();
  const touch = useMediaQuery('(hover: none)');
  const lb = useLightbox();
  const cta = useSlotGroup('amenitieslist.cta', ctaDef);
  const photoSlot = useSlotGroup('amenitieslist.photo', photoDef);
  const all = useCollectionBinding<Amenity>('amenitieslist.items', { collection: 'amenities' });
  const [active, setActive] = useState<number | null>(null);

  const photos = galleryPhotos<Photo>(p.photo_gallery);

  const amenities = all.filter((a) => clean(a.title));
  const picked: Amenity[] = [];
  PRIORITY.forEach((rx) => {
    const a = amenities.find((x) => rx.test(x.title || '') && !picked.includes(x));
    if (a) picked.push(a);
  });
  ['Apartment', 'Community'].forEach((gr) => amenities.filter((a) => a.group === gr && !picked.includes(a)).forEach((a) => picked.length < 6 && picked.push(a)));
  const six = picked.slice(0, 6);
  const groups: Record<string, Amenity[]> = {};
  six.forEach((a) => (groups[groupLabel(a.group)] = groups[groupLabel(a.group)] || []).push(a));
  const groupNames = Object.keys(groups).sort((a) => (a === 'Community' ? -1 : 1));

  const chosen = photoSlot.image && photoSlot.image !== photos[0]?.image ? { src: photoSlot.image, alt: photoSlot.alt || '' } : null;
  const base = photos[6] || photos[0];
  const activePhoto = active != null ? amenityPhoto(six[active], photos) : null;
  const featured = clean(p.property_featured_image);
  const shown = activePhoto || chosen || (base ? { src: base.image, alt: base.alt_text || '' } : featured ? { src: featured, alt: '' } : null);
  const lbItems = six.flatMap((a) => {
    const ph = amenityPhoto(a, photos);
    return ph ? [{ ...ph, caption: a.title }] : [];
  });

  const pick = (i: number) => {
    setActive(i);
    if (touch) {
      const src = amenityPhoto(six[i], photos)?.src;
      lb.open(Math.max(0, lbItems.findIndex((x) => x.src === src)));
    }
  };

  if (!six.length) return null;

  return (
    <section className="section amenities" id="amenities">
      <div className="section__head" data-reveal>
        <div className="section__lead-block">
          <RichTextContent path="amenitieslist.eyebrow" as="div" className="eyebrow" />
          <RichTextContent path="amenitieslist.title" as="h2" className="h2" />
        </div>
        <RichTextContent path="amenitieslist.lead" as="p" className="section__lead" />
      </div>
      <div className="flex items-stretch gap-16 max-[1199px]:flex-col">
        <EditableSlotGroup
          groupPath="amenitieslist.photo"
          definition={photoDef}
          className={shown ? 'photo photo--tall [&_img]:opacity-100 [&_img]:[transition:opacity_var(--q-motion-slow)_var(--q-ease-in-out)]' : ''}
        >
          {shown && <VibesitesImage key={shown.src} url={shown.src} alt={shown.alt} sizes="(min-width: 1200px) 592px, 100vw" />}
        </EditableSlotGroup>
        <EditableCollectionBinding bindingPath="amenitieslist.items" label="Amenities" className="flex grow flex-col gap-10">
          {groupNames.map((g, gi) => (
            <div className="flex flex-col" key={g} data-reveal style={stagger(gi + 1)}>
              <div className="eyebrow border-b border-b-[--q-secondary] pb-3">{g}</div>
              {groups[g].map((a) => {
                const i = six.indexOf(a);
                const ph = amenityPhoto(a, photos);
                return ph ? (
                  <button
                    key={i}
                    className={`group/row flex w-full cursor-pointer items-center gap-6 rounded-none border-b border-b-[--q-border] py-3 text-left font-heading text-[22px] leading-7 [background:none] [border-left:0] [border-right:0] [border-top:0] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[--q-primary-on-paper] max-[767px]:py-2 max-[767px]:text-[19px] max-[767px]:leading-[25px] ${
                      active === i ? 'text-[--q-primary-on-paper]' : 'text-inherit hover:text-[--q-primary-on-paper]'
                    }`}
                    type="button"
                    aria-label={`${a.title}: show the photo`}
                    onClick={() => pick(i)}
                    onMouseEnter={() => !touch && setActive(i)}
                    onFocus={() => !touch && setActive(i)}
                  >
                    <span className="grow">{a.title}</span>
                    <VibesitesImage
                      url={ph.src}
                      alt=""
                      sizes="52px"
                      className={`h-9 w-[52px] shrink-0 rounded-sm object-cover outline outline-2 outline-offset-2 [transition:outline-color_var(--q-motion-fast),transform_var(--q-motion-normal)_var(--q-ease-out)] motion-reduce:[transition:none] max-[767px]:h-[35px] max-[767px]:w-12 ${
                        active === i
                          ? '[transform:scale(1.06)] outline-[--q-primary-on-paper]'
                          : 'outline-transparent group-hover/row:outline-[--q-primary-on-paper] group-hover/row:[transform:scale(1.06)]'
                      }`}
                    />
                  </button>
                ) : (
                  <div className="flex items-baseline gap-6 border-b border-b-[--q-border] py-4 font-heading text-[22px] leading-7 max-[767px]:py-[13px] max-[767px]:text-[19px] max-[767px]:leading-[25px]" key={i}>
                    <span className="grow">{a.title}</span>
                  </div>
                );
              })}
            </div>
          ))}
        </EditableCollectionBinding>
      </div>
      <div className="section__cta" data-reveal>
        <div className="section__buttons">
          <EditableSlotGroup groupPath="amenitieslist.cta" definition={ctaDef}>
            <a className="btn btn-primary" href={cta.url || PATHS.amenities} target={cta._target || '_self'}>
              {cta.label || 'See All Amenities'} <span className="btn__arrow">→</span>
            </a>
          </EditableSlotGroup>
        </div>
      </div>
      <Lightbox items={lbItems} index={lb.index} onClose={lb.close} onIndex={lb.setIndex} />
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'AmenitiesList',
  displayName: 'Amenities',
  tokens: {
    'amenitieslist.eyebrow': { label: 'Eyebrow', type: 'richtext', defaultValue: '<p>Amenities · your home and the community</p>' },
    'amenitieslist.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Everyday comfort, built in</p>',
      suggestedFields: ['property_home_amenities_blurb', 'property_amenities_blurb'],
      generator: {
        prompt: "A short headline for this property's amenities, true to what it actually offers. No closing period.",
        context: 'H2 of the Amenities section on the home page, above the lead and the six-amenity list.',
        length: '3–7 words',
      },
    },
    'amenitieslist.lead': {
      label: 'Lead',
      description: 'Mapped to the Home Amenities Blurb field. Type over it to replace it, or pick another field from the suggestions.',
      type: 'richtext',
      defaultField: 'property_home_amenities_blurb',
      defaultValue: '<p>Comfort in every home and space to unwind across the community at <span data-type="field-token" data-field="property_name"></span>.</p>',
      suggestedFields: ['property_home_amenities_blurb', 'property_amenities_blurb'],
      generator: {
        prompt: 'A lead that sums up what living with these amenities is like, in the home and around the community. Name only amenities this property really has. Point readers to the amenities page if it exists.',
        context: 'Lead under the Amenities headline on the home page, beside the six-amenity list. Do not repeat the headline.',
        length: '1–2 sentences',
      },
    },
  },
  slotGroups: {
    'amenitieslist.photo': photoDef,
    'amenitieslist.cta': ctaDef,
  },
  collectionBindings: {
    'amenitieslist.items': {
      label: 'Amenities',
      collection: 'amenities',
    },
  },
};

(AmenitiesList as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default AmenitiesList;
